/**
 * Save Actor Result Action
 *
 * Synchronous action that saves actor success result to context.
 * Used in onDone transitions to persist actor output.
 */

import { datadogLogs } from "@datadog/browser-logs";
import { assign } from "xstate";

/**
 * Action metadata
 */
export interface ActionMetadata {
  displayName: string;
  description: string;
  category: "context" | "logging" | "analytics" | "spawn" | "navigation";
}

/**
 * Action with metadata wrapper
 */
export interface ActionWithMetadata {
  impl: ReturnType<typeof assign>;
  metadata: ActionMetadata;
}

/**
 * Deep merge helper for nested objects
 *
 * Recursively merges source into target, preserving existing data.
 * Returns a new object without mutating the originals.
 */
function deepMerge<T extends Record<string, unknown>>(
  target: T | null | undefined,
  source: Partial<T> | null | undefined,
): T {
  // If source is not an object or is null, return it as-is (or target if source is invalid)
  if (typeof source !== "object" || source === null || Array.isArray(source)) {
    return (source ?? target ?? {}) as T;
  }

  // If target is not an object, replace with source
  if (typeof target !== "object" || target === null || Array.isArray(target)) {
    return source as T;
  }

  // Merge objects immutably
  const result: Record<string, unknown> = { ...target };

  for (const key in source) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      const targetValue = target[key];
      const sourceValue = source[key];

      // Recursively merge if both are objects
      if (
        typeof targetValue === "object" &&
        targetValue !== null &&
        !Array.isArray(targetValue) &&
        typeof sourceValue === "object" &&
        sourceValue !== null &&
        !Array.isArray(sourceValue)
      ) {
        result[key] = deepMerge(
          targetValue as Record<string, unknown>,
          sourceValue as Record<string, unknown>,
        );
      } else if (sourceValue !== undefined) {
        // Otherwise replace with source value (skip undefined to preserve existing context values)
        result[key] = sourceValue;
      }
    }
  }

  return result as T;
}

/**
 * Save Actor Result Action
 *
 * Saves successful actor output from event.output to context.
 * Performs deep immutable merge to preserve existing nested data.
 *
 * Example use cases:
 * - SMS verification: adds userId, otpTime while preserving phone/email
 * - Insurance verification: adds status, error while preserving payer/memberId/state
 * - Any actor that augments existing context data
 *
 * Example in flow JSON:
 * ```json
 * {
 *   "invoke": {
 *     "actor": "sendSMS",
 *     "onDone": [{
 *       "target": "otpInput",
 *       "actions": [{ "type": "saveActorResult" }]
 *     }]
 *   }
 * }
 * ```
 */
export const saveActorResult: ActionWithMetadata = {
  // biome-ignore lint/suspicious/noExplicitAny: XState action params are complex
  impl: assign(({ event, context }: any) => {
    // XState passes actor result in event.output for invoke onDone
    const output = event.output;

    datadogLogs.logger.info("Actor completed", {
      actorId: event.actorId ?? "unknown",
      hasOutput: !!output,
      outputType: typeof output,
      outputKeys:
        output && typeof output === "object" ? Object.keys(output) : null,
      tags: ["KIOSK", "STATE", "ACTOR_RESULT"],
    });

    if (!output || typeof output !== "object") {
      return output || {};
    }

    // Deep merge output into context to preserve existing nested data
    return deepMerge(context, output);
  }),
  metadata: {
    displayName: "saveActorResult",
    description:
      "Save actor success result to context with deep immutable merge",
    category: "context",
  },
};
